import { useMemo, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { HERO_LIST } from "../utils/queries";
import { useIsDark, useToggleDark } from "../context/ThemeContext";
import {
    useIncludeThirdParty,
    useToggleThirdParty,
} from "../context/ThirdPartyContext";
import formatName from "../utils/formatName";
import useHeroPath from "../utils/useHeroPath";
import "../styles/NavBar.css";

const ROLE_ORDER = ["Vanguard", "Duelist", "Strategist"];

export default function NavBar() {
    const heroPath = useHeroPath();
    const isDark = useIsDark();
    const toggleDark = useToggleDark();
    const includeThirdParty = useIncludeThirdParty();
    const toggleThirdParty = useToggleThirdParty();

    const { data } = useQuery(HERO_LIST, {
        variables: { includeThirdParty },
    });

    const heroesByRole = useMemo(() => {
        const groups = { Vanguard: [], Duelist: [], Strategist: [] };
        data?.heroList?.forEach(({ hero, role }) => {
            if (groups[role]) groups[role].push(hero);
        });
        Object.values(groups).forEach((arr) => arr.sort());
        return groups;
    }, [data]);

    const [heroesOpen, setHeroesOpen] = useState(false);
    const heroesWrapRef = useRef(null);
    const closeTimer = useRef(null);
    const [dropPos, setDropPos] = useState({ top: 0, right: 0 });

    const navRef = useRef(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [navBottom, setNavBottom] = useState(0);

    const scheduleClose = useCallback(() => {
        closeTimer.current = setTimeout(() => setHeroesOpen(false), 150);
    }, []);

    const cancelClose = useCallback(() => {
        clearTimeout(closeTimer.current);
    }, []);

    const handleWrapEnter = useCallback(() => {
        cancelClose();
        if (heroesWrapRef.current) {
            const rect = heroesWrapRef.current.getBoundingClientRect();
            setDropPos({
                top: rect.bottom,
                right: window.innerWidth - rect.right + 2,
            });
        }
        setHeroesOpen(true);
    }, [cancelClose]);

    const toggleMobileMenu = useCallback(() => {
        if (navRef.current) {
            setNavBottom(navRef.current.getBoundingClientRect().bottom);
        }
        setMobileMenuOpen((o) => !o);
    }, []);

    const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

    return (
        <nav className="navbar" ref={navRef}>
            <span className="navbar-title">Rivals Analyti.cx</span>
            <div className="navbar-links">
                <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                        "nav-link" + (isActive ? " active" : "")
                    }
                >
                    Meta
                </NavLink>
                <NavLink
                    to="/history"
                    className={({ isActive }) =>
                        "nav-link" + (isActive ? " active" : "")
                    }
                >
                    History
                </NavLink>
                <NavLink
                    to="/teamups"
                    className={({ isActive }) =>
                        "nav-link" + (isActive ? " active" : "")
                    }
                >
                    Team-Ups
                </NavLink>
                <div
                    className="nav-heroes-wrap"
                    ref={heroesWrapRef}
                    onMouseEnter={handleWrapEnter}
                    onMouseLeave={scheduleClose}
                >
                    <button className="nav-link nav-heroes-btn">
                        Heroes ▾
                    </button>
                </div>
            </div>
            <button
                className={`nav-tp-btn filter-btn${
                    includeThirdParty ? " active" : ""
                }`}
                onClick={toggleThirdParty}
                title={`Third-party data is currently ${
                    includeThirdParty
                        ? "included when official data is stale"
                        : "excluded"
                }.
Third-party data has some accuracy limitations (see FAQ), but provides hero ban stats and is updated more frequently.`}
            >
                3P
            </button>
            <div className="filter-btn-group">
                <button
                    className={`filter-btn${!isDark ? " active" : ""}`}
                    onClick={() => isDark && toggleDark()}
                    aria-label="Light mode"
                >
                    {"☀︎"}
                </button>
                <button
                    className={`filter-btn${isDark ? " active" : ""}`}
                    onClick={() => !isDark && toggleDark()}
                    aria-label="Dark mode"
                >
                    ☾
                </button>
            </div>
            <button
                className="nav-hamburger"
                onClick={toggleMobileMenu}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
            >
                {mobileMenuOpen ? "✕" : "☰"}
            </button>
            {heroesOpen &&
                createPortal(
                    <div
                        className={`nav-heroes-dropdown${
                            isDark ? " dark" : ""
                        }`}
                        style={{
                            position: "fixed",
                            top: dropPos.top,
                            right: dropPos.right,
                        }}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        {ROLE_ORDER.map((role) => (
                            <div key={role} className="nav-heroes-col">
                                <div
                                    className={`nav-heroes-role role-text-${role.toLowerCase()}`}
                                >
                                    {role}
                                </div>
                                {heroesByRole[role].map((hero) => (
                                    <NavLink
                                        key={hero}
                                        to={heroPath(hero, role)}
                                        onClick={() => setHeroesOpen(false)}
                                        className={({ isActive }) =>
                                            "nav-hero-link" +
                                            (isActive ? " active" : "")
                                        }
                                    >
                                        {formatName(hero)}
                                    </NavLink>
                                ))}
                            </div>
                        ))}
                    </div>,
                    document.body,
                )}
            {mobileMenuOpen &&
                createPortal(
                    <>
                        <div
                            className="nav-mobile-backdrop"
                            onClick={closeMobileMenu}
                        />
                        <div
                            className={`nav-mobile-menu${
                                isDark ? " dark" : ""
                            }`}
                            style={{
                                top: navBottom,
                                maxHeight: `calc(100vh - ${navBottom}px)`,
                            }}
                        >
                            <NavLink
                                to="/"
                                end
                                className={({ isActive }) =>
                                    "nav-mobile-link" +
                                    (isActive ? " active" : "")
                                }
                                onClick={closeMobileMenu}
                            >
                                Meta
                            </NavLink>
                            <NavLink
                                to="/history"
                                className={({ isActive }) =>
                                    "nav-mobile-link" +
                                    (isActive ? " active" : "")
                                }
                                onClick={closeMobileMenu}
                            >
                                History
                            </NavLink>
                            <NavLink
                                to="/teamups"
                                className={({ isActive }) =>
                                    "nav-mobile-link" +
                                    (isActive ? " active" : "")
                                }
                                onClick={closeMobileMenu}
                            >
                                Team-Ups
                            </NavLink>
                            <NavLink
                                to="/faq"
                                className={({ isActive }) =>
                                    "nav-mobile-link" +
                                    (isActive ? " active" : "")
                                }
                                onClick={closeMobileMenu}
                            >
                                FAQ
                            </NavLink>
                            <div className="nav-mobile-divider" />
                            {ROLE_ORDER.map((role) => (
                                <div
                                    key={role}
                                    className="nav-mobile-role-group"
                                >
                                    <div
                                        className={`nav-heroes-role role-text-${role.toLowerCase()}`}
                                    >
                                        {role}
                                    </div>
                                    <div className="nav-mobile-hero-grid">
                                        {heroesByRole[role].map((hero) => (
                                            <NavLink
                                                key={hero}
                                                to={heroPath(hero, role)}
                                                onClick={closeMobileMenu}
                                                className={({ isActive }) =>
                                                    "nav-hero-link" +
                                                    (isActive ? " active" : "")
                                                }
                                            >
                                                {formatName(hero)}
                                            </NavLink>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>,
                    document.body,
                )}
        </nav>
    );
}
